import React, { useState, useEffect } from "react";
import {
  Container,
  Paper,
  Box,
  TextField,
  Button,
  Typography,
  Snackbar,
  Alert,
  Autocomplete,
  CircularProgress
} from "@mui/material";
import axios from "axios";
import { useParams, useNavigate } from "react-router-dom";
import BaseURL from "../../api/AppURL";

export default function EditRole() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [values, setValues] = useState({
    name: "",
  });
  const [roleOptions, setRoleOptions] = useState([]);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    severity: "success",
    message: "",
  });

  // Snackbar close handler
  const handleSnackbarClose = (_, reason) => {
    if (reason === "clickaway") return;
    setSnackbar((s) => ({ ...s, open: false }));
  };

  // Input field change handler
  const handleChange = (field) => (e) => {
    setValues((v) => ({ ...v, [field]: e.target.value }));
    setErrors((err) => ({ ...err, [field]: null }));
  };

  // Validation
  const validate = () => {
    const err = {};
    if (!values.name.trim()) err.name = "Full Name is required";
    setErrors(err);
    return Object.keys(err).length === 0;
  };

  // Load admin data
  useEffect(() => {
    const fetchData = async () => {
      setFetching(true);
      try {
        const token = localStorage.getItem("auth_token");
        // fetch user info and roles
        const res = await axios.get(
          `${BaseURL}/edit/roles/${id}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              Accept: "application/json",
            },
          }
        );

        if (res.data && res.data.status === "success") {
      
            const role = res.data.role;
            setValues({
              name: role.name || "",
            });
          
        }
      } catch (err) {
        setSnackbar({
          open: true,
          severity: "error",
          message: "Failed to load user data",
        });
      } finally {
        setFetching(false);
      }
    };
    if (id) fetchData();
  }, [id]);

  // Submit handler
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) {
      setSnackbar({
        open: true,
        severity: "error",
        message: "Fix validation errors",
      });
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem("auth_token");
      // build payload
      const postData = {
        name: values.name,
      };
    

      const res = await axios.put(
        `${BaseURL}/update/roles/${id}`,
        postData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: "application/json",
          },
        }
      );

      setSnackbar({
        open: true,
        severity: "success",
        message: res.data.message || "Admin updated",
      });
      setTimeout(() => {
        navigate("/admin/all_role");
      }, 1200);
    } catch (err) {
      const msg = err?.response?.data?.message || "Failed to update admin";
      setSnackbar({ open: true, severity: "error", message: msg });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="sm" sx={{ mt: 4 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Typography variant="h6" gutterBottom>
          Edit Role
        </Typography>
        {fetching ? (
          <Box sx={{ display: "flex", justifyContent: "center", my: 5 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box
            component="form"
            onSubmit={handleSubmit}
            noValidate
            sx={{ display: "flex", flexDirection: "column", gap: 2 }}
          >
           
            <TextField
              label="Full Name"
              value={values.name}
              onChange={handleChange("name")}
              required
              error={!!errors.name}
              helperText={errors.name}
            />
            
          
            <Button type="submit" variant="contained" disabled={loading}>
              {loading ? "Updating..." : "Save Changes"}
            </Button>
          </Box>
        )}
      </Paper>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
}